import type { PricingLineItem } from "@reno-estimator/core";

/**
 * Initial sample Canadian pricing dataset.
 *
 * EVERY figure in this file is an "Administrator supplied sample value"
 * per the project brief - a plausible planning-level placeholder, not a
 * number backed by any cited industry source. `confidenceLevel` is set to
 * "administrator_sample_value" and `sourceId` is null on every record;
 * `validatePricingLineItem` (see ../schema/validation.ts) enforces that
 * combination and will fail any record that tries to claim sourcing it
 * doesn't have. See ./README.md for what it takes to promote a record to
 * "estimated" or "verified".
 */

export const SAMPLE_PRICING_VERSION = "2026.08-sample-1";
export const SAMPLE_EFFECTIVE_DATE = "2026-08-24";
const SAMPLE_TIMESTAMP = "2026-08-24T00:00:00.000Z";

export const SAMPLE_DISCLOSURE =
  "Administrator supplied sample value. Not sourced from any verified Canadian pricing dataset - for architecture/testing purposes only. Must be replaced before this catalog is used for a real customer-facing estimate.";

type SampleItemInput = Omit<
  PricingLineItem,
  "pricingVersion" | "effectiveDate" | "sourceId" | "sourceDate" | "confidenceLevel" | "adminNotes" | "createdAt" | "modifiedAt"
> & { adminNotes?: string };

function sampleItem(input: SampleItemInput): PricingLineItem {
  return {
    ...input,
    pricingVersion: SAMPLE_PRICING_VERSION,
    effectiveDate: SAMPLE_EFFECTIVE_DATE,
    sourceId: null,
    sourceDate: null,
    confidenceLevel: "administrator_sample_value",
    adminNotes: input.adminNotes ? `${SAMPLE_DISCLOSURE} ${input.adminNotes}` : SAMPLE_DISCLOSURE,
    createdAt: SAMPLE_TIMESTAMP,
    modifiedAt: SAMPLE_TIMESTAMP
  };
}

const STANDARD_FINISH_MULTIPLIERS = { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 };

// ---------------------------------------------------------------------------
// Kitchen
// ---------------------------------------------------------------------------

export const kitchenSampleItems: PricingLineItem[] = [
  sampleItem({
    id: "kitchen_base_renovation",
    projectType: "kitchen",
    category: "base_renovation",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 120, typical: 180, high: 260, minimum: 90, maximum: 350 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_cabinetry_stock",
    projectType: "kitchen",
    category: "cabinetry",
    subcategory: "stock",
    unit: "linear_ft",
    percentageOfCategory: null,
    costs: { low: 200, typical: 300, high: 400, minimum: 150, maximum: 500 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.9, standard: 1.0, premium: 1.2, luxury: 1.4 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_cabinetry_semi_custom",
    projectType: "kitchen",
    category: "cabinetry",
    subcategory: "semi_custom",
    unit: "linear_ft",
    percentageOfCategory: null,
    costs: { low: 400, typical: 550, high: 750, minimum: 350, maximum: 900 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.9, standard: 1.0, premium: 1.2, luxury: 1.4 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_cabinetry_full_custom",
    projectType: "kitchen",
    category: "cabinetry",
    subcategory: "full_custom",
    unit: "linear_ft",
    percentageOfCategory: null,
    costs: { low: 750, typical: 1100, high: 1600, minimum: 600, maximum: 2200 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.95, standard: 1.0, premium: 1.25, luxury: 1.6 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_countertop_laminate",
    projectType: "kitchen",
    category: "countertops",
    subcategory: "laminate",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 25, typical: 40, high: 60, minimum: 20, maximum: 80 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_countertop_quartz",
    projectType: "kitchen",
    category: "countertops",
    subcategory: "quartz",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 70, typical: 100, high: 140, minimum: 60, maximum: 180 },
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_countertop_granite",
    projectType: "kitchen",
    category: "countertops",
    subcategory: "granite",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 65, typical: 95, high: 135, minimum: 55, maximum: 175 },
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_appliances_package",
    projectType: "kitchen",
    category: "appliances",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 4000, typical: 8000, high: 16000, minimum: 2500, maximum: 30000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.6, standard: 1.0, premium: 1.8, luxury: 3.2 }, regionalOverride: null },
    adminNotes: "Represents a full replacement package (fridge, range, dishwasher, hood); adjust for partial replacements."
  }),
  sampleItem({
    id: "kitchen_flooring",
    projectType: "kitchen",
    category: "flooring",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 8, typical: 14, high: 22, minimum: 5, maximum: 35 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_backsplash",
    projectType: "kitchen",
    category: "backsplash",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 15, typical: 25, high: 45, minimum: 10, maximum: 70 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_electrical",
    projectType: "kitchen",
    category: "electrical",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1200, typical: 2500, high: 5000, minimum: 600, maximum: 9000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.15, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_plumbing",
    projectType: "kitchen",
    category: "plumbing",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1000, typical: 2200, high: 4500, minimum: 500, maximum: 8000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.15, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.25, luxury: 1.6 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_demolition",
    projectType: "kitchen",
    category: "demolition",
    subcategory: null,
    unit: "percentage",
    percentageOfCategory: "base_renovation",
    costs: { low: 6, typical: 9, high: 14, minimum: 4, maximum: 20 },
    components: { labourPercent: 90, materialPercent: 10 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Priced as a percentage of the base renovation cost line item, rather than a flat or per-sqft figure."
  }),
  sampleItem({
    id: "kitchen_island",
    projectType: "kitchen",
    category: "island",
    subcategory: null,
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 2500, typical: 5000, high: 9000, minimum: 1500, maximum: 15000 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.35, luxury: 1.9 }, regionalOverride: null }
  }),
  sampleItem({
    id: "kitchen_structural_changes",
    projectType: "kitchen",
    category: "structural_changes",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2000, typical: 6000, high: 15000, minimum: 800, maximum: 30000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.3, finishLevel: {}, regionalOverride: null },
    adminNotes: "Wide range reflects that structural scope (e.g. load-bearing wall removal) varies enormously by site."
  })
];

// ---------------------------------------------------------------------------
// Bathroom
// ---------------------------------------------------------------------------

export const bathroomSampleItems: PricingLineItem[] = [
  sampleItem({
    id: "bathroom_base_renovation",
    projectType: "bathroom",
    category: "base_renovation",
    subcategory: null,
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 8000, typical: 14000, high: 22000, minimum: 5000, maximum: 35000 },
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_vanity",
    projectType: "bathroom",
    category: "vanity",
    subcategory: null,
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 600, typical: 1500, high: 3500, minimum: 350, maximum: 6000 },
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.5 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_toilet",
    projectType: "bathroom",
    category: "toilet",
    subcategory: null,
    unit: "fixture",
    percentageOfCategory: null,
    costs: { low: 400, typical: 700, high: 1200, minimum: 300, maximum: 2000 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_shower_stall",
    projectType: "bathroom",
    category: "shower_tub",
    subcategory: "shower_stall",
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 3000, typical: 5500, high: 9000, minimum: 2000, maximum: 15000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.2 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_tub_shower_combo",
    projectType: "bathroom",
    category: "shower_tub",
    subcategory: "tub_shower_combo",
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 2200, typical: 4000, high: 6500, minimum: 1500, maximum: 10000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_tile",
    projectType: "bathroom",
    category: "tile",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 12, typical: 20, high: 35, minimum: 8, maximum: 55 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_glass",
    projectType: "bathroom",
    category: "glass",
    subcategory: null,
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 800, typical: 1800, high: 3500, minimum: 500, maximum: 6000 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.4 }, regionalOverride: null },
    adminNotes: "Represents a frameless or semi-frameless shower glass enclosure; a shower curtain instead of glass has no cost here."
  }),
  sampleItem({
    id: "bathroom_plumbing",
    projectType: "bathroom",
    category: "plumbing",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 3000, high: 6000, minimum: 800, maximum: 10000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.2, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.25, luxury: 1.6 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_electrical",
    projectType: "bathroom",
    category: "electrical",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 800, typical: 1800, high: 3500, minimum: 400, maximum: 6000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_heated_floor",
    projectType: "bathroom",
    category: "heated_floor",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    // Phase 20 audit fix: raised from the original 18 typical - at this rate the total
    // cost added (over a typical bathroom's floor area) was small enough to vanish
    // entirely under the applicable rounding increment (see rounding.ts), so selecting
    // heated flooring could produce an identical rounded headline estimate to not
    // selecting it, despite genuinely costing more. Represents an electric radiant
    // system including a new thermostat/timer, not just the heating cable itself.
    costs: { low: 35, typical: 55, high: 85, minimum: 25, maximum: 120 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Optional add-on line item; only applies if the user selected in-floor heating."
  }),
  sampleItem({
    id: "bathroom_ventilation",
    projectType: "bathroom",
    category: "ventilation",
    subcategory: null,
    unit: "fixture",
    percentageOfCategory: null,
    costs: { low: 300, typical: 600, high: 1200, minimum: 200, maximum: 2000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItem({
    id: "bathroom_demolition",
    projectType: "bathroom",
    category: "demolition",
    subcategory: null,
    unit: "percentage",
    percentageOfCategory: "base_renovation",
    costs: { low: 7, typical: 10, high: 15, minimum: 5, maximum: 22 },
    components: { labourPercent: 90, materialPercent: 10 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  })
];

// ---------------------------------------------------------------------------
// Basement
// ---------------------------------------------------------------------------

export const basementSampleItems: PricingLineItem[] = [
  sampleItem({
    id: "basement_finishing_base",
    projectType: "basement",
    category: "finishing_base",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 35, typical: 55, high: 80, minimum: 25, maximum: 110 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null },
    adminNotes: "Base square-foot finishing cost, before optional additions (bathroom, wet bar, egress, etc.)."
  }),
  sampleItem({
    id: "basement_framing_drywall",
    projectType: "basement",
    category: "framing_drywall",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 8, typical: 13, high: 20, minimum: 6, maximum: 28 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItem({
    id: "basement_insulation",
    projectType: "basement",
    category: "insulation",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 3, typical: 5, high: 9, minimum: 2, maximum: 14 },
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItem({
    id: "basement_flooring",
    projectType: "basement",
    category: "flooring",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 6, typical: 10, high: 16, minimum: 4, maximum: 25 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null }
  }),
  sampleItem({
    id: "basement_electrical",
    projectType: "basement",
    category: "electrical",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 3500, high: 7000, minimum: 800, maximum: 12000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  sampleItem({
    id: "basement_hvac",
    projectType: "basement",
    category: "hvac",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 4000, high: 9000, minimum: 800, maximum: 15000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.15, finishLevel: {}, regionalOverride: null },
    adminNotes: "Extending/adding ductwork or supplemental heating/cooling to the finished basement space."
  }),
  sampleItem({
    id: "basement_bathroom_addition",
    projectType: "basement",
    category: "bathroom",
    subcategory: null,
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 9000, typical: 15000, high: 24000, minimum: 6000, maximum: 35000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.2, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null },
    adminNotes: "Adding a new bathroom (not present before) is materially more expensive than finishing an existing rough-in."
  }),
  sampleItem({
    id: "basement_wet_bar",
    projectType: "basement",
    category: "kitchen_wet_bar",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 4000, typical: 9000, high: 18000, minimum: 2500, maximum: 30000 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.3 }, regionalOverride: null }
  }),
  sampleItem({
    id: "basement_bedroom",
    projectType: "basement",
    category: "bedroom",
    subcategory: null,
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 3000, typical: 5500, high: 9000, minimum: 2000, maximum: 14000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS, regionalOverride: null },
    adminNotes: "Framing, closet, and finish for a basement bedroom, excluding the egress window itself (see basement_egress_window)."
  }),
  sampleItem({
    id: "basement_egress_window",
    projectType: "basement",
    category: "egress",
    subcategory: null,
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 3500, typical: 6000, high: 10000, minimum: 2500, maximum: 15000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.2, finishLevel: {}, regionalOverride: null },
    adminNotes: "Required for a basement bedroom in most Canadian building codes; priced per window well."
  }),
  sampleItem({
    id: "basement_ceiling",
    projectType: "basement",
    category: "ceiling",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 3, typical: 5, high: 9, minimum: 2, maximum: 14 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Covers both drywall ceilings and drop/suspended ceiling systems at different finish levels."
  }),
  sampleItem({
    id: "basement_demolition",
    projectType: "basement",
    category: "demolition",
    subcategory: null,
    unit: "percentage",
    percentageOfCategory: "finishing_base",
    costs: { low: 5, typical: 8, high: 13, minimum: 3, maximum: 20 },
    components: { labourPercent: 90, materialPercent: 10 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Only applies when finishing an already-partially-finished or previously-damaged basement."
  })
];

export const allSamplePricingLineItems: PricingLineItem[] = [
  ...kitchenSampleItems,
  ...bathroomSampleItems,
  ...basementSampleItems
];
